import React, { useEffect, useRef, useState } from "react";
import { FilePdfOutlined, LinkOutlined } from "@ant-design/icons";
import HandlingFeePicker from "../../../Components/Agent/PaymentAgent/HandlingFeePicker/HandlingFeePicker";
import { formatIls } from "../../packageProfileMockup/mockPackageProfileData";
import { openVacationPackageStyleQuotePdf } from "../../../utils/openVacationPackageStyleQuotePdf";
import { buildPublicOfferLinkSnapshot } from "../../../utils/dynamicPackage/buildPublicOfferLinkSnapshot";
import { createPublicOfferLinkRequest } from "../../../services/publicOfferLinksApi";
import { getUserDetails } from "../../../services/authService";
import { quoteHubwayzCustomerPrice } from "../../../services/hubwayzHotelsApi";
import { formatHubwayzSaleDisplay } from "./hubwayzRoomSaleDisplay";
import {
  hubwayzCommissionMatchesRequest,
  previewHubwayzCommissionQuote,
  serverQuotePriceView,
} from "./hubwayzVerifiedQuote";
import { hubwayzHotelShareUrl } from "./hubwayzQuoteAddedCommission";

/**
 * Agent tools above the Hubwayz order summary.
 * Commission, PDF, and hotel link reuse the existing picker and quote PDF.
 * The booking charge stays the verified agent net. Commission type and value
 * are sent to the server, which returns the customer sale.
 */
export default function HubwayzRoomAgentTools({
  hotelId,
  hotelName,
  city,
  country,
  checkIn,
  checkOut,
  roomName,
  boardLabel,
  capacityLabel,
  quote,
  displayCurrency = "",
  cancellation = "",
  nights = null,
  gallery = [],
  onToast,
  onCustomerQuote,
  onCommissionChange,
  fallbackSearchId = "",
  initialCommissionIls = 0,
}) {
  const [handleFeeData, setHandleFeeData] = useState(() => ({
    type: "+",
    value: Number(initialCommissionIls) > 0 ? Number(initialCommissionIls) : 0,
  }));
  const [serverQuote, setServerQuote] = useState(null);
  const [commissionPending, setCommissionPending] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [offerBusy, setOfferBusy] = useState(false);
  const [offerUrl, setOfferUrl] = useState("");
  const commissionDirty = useRef(Number(initialCommissionIls) > 0);

  const onCustomerQuoteRef = useRef(onCustomerQuote);
  onCustomerQuoteRef.current = onCustomerQuote;
  const onToastRef = useRef(onToast);
  onToastRef.current = onToast;
  const quoteRef = useRef(quote);
  quoteRef.current = quote;

  const quoteOfferCode = quote?.offerCode || "";
  const quoteSearchId = quote?.searchId || fallbackSearchId || "";
  const quoteNetIls = Number(quote?.agentNetIls) || 0;
  const commissionType = handleFeeData.type === "%" ? "%" : "+";
  const commissionValue = Number(handleFeeData.value) || 0;

  useEffect(() => {
    const currentQuote = quoteRef.current;
    const shouldLock = commissionDirty.current || commissionValue > 0;
    if (!shouldLock) {
      setServerQuote(null);
      setCommissionPending(false);
      onCustomerQuoteRef.current?.({
        ok: currentQuote?.binding === true,
        addedCommissionIls: 0,
        commissionLocked: false,
      });
      return undefined;
    }
    const preview = previewHubwayzCommissionQuote(
      currentQuote,
      commissionType,
      commissionValue
    );
    if (preview) {
      setServerQuote(preview);
      onCustomerQuoteRef.current?.(preview);
    }
    if (!quoteOfferCode || !quoteSearchId) {
      setCommissionPending(false);
      return undefined;
    }
    let cancelled = false;
    setCommissionPending(true);
    quoteHubwayzCustomerPrice({
      offerCode: quoteOfferCode,
      searchId: quoteSearchId,
      displayCurrency,
      commissionType,
      commissionValue,
    })
      .then((data) => {
        if (cancelled) return;
        const view = serverQuotePriceView(data);
        if (!hubwayzCommissionMatchesRequest(view, commissionType, commissionValue)) {
          onToastRef.current?.("עמלת הסוכן לא אושרה בשרת. הסכום במסך נשאר לפי העמלה שנבחרה.");
          return;
        }
        const locked = { ...view, commissionLocked: true, addedCommissionIls: 0, ok: true };
        setServerQuote(locked);
        onCustomerQuoteRef.current?.(locked);
      })
      .catch(() => {
        if (cancelled) return;
        onToastRef.current?.("עמלת הסוכן לא אושרה בשרת. הסכום במסך נשאר לפי העמלה שנבחרה.");
      })
      .finally(() => {
        if (!cancelled) setCommissionPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, [quoteOfferCode, quoteSearchId, quoteNetIls, commissionType, commissionValue, displayCurrency]);

  const hotelUrl =
    typeof window === "undefined"
      ? hubwayzHotelShareUrl(hotelId)
      : hubwayzHotelShareUrl(hotelId, {
          origin: window.location.origin,
          search: window.location.search,
        });

  const updateHandleFeeData = (type, value) => {
    commissionDirty.current = true;
    const next = {
      type: type === "%" ? "%" : "+",
      value: Number(value) || 0,
    };
    setHandleFeeData(next);
    onCommissionChange?.(next);
  };

  const shown = serverQuote?.commissionLocked ? serverQuote : quote;
  const orderCurrency = shown?.customerSaleCurrency || shown?.dealCurrency || "";
  const markupIls = Number(shown?.agentMarkupIls) || 0;
  const markupOrder = Number(shown?.agentMarkupAmount);
  const saleOrder = Number(shown?.customerSaleAmount);
  const netOrder = Number(shown?.agentNetAmount);
  const orderMarkupHidesIls =
    Number.isFinite(markupOrder) && markupOrder === 0 && markupIls > 0;
  const orderSaleHidesMarkup =
    Number.isFinite(saleOrder) &&
    Number.isFinite(netOrder) &&
    Math.abs(saleOrder - netOrder) < 0.0001 &&
    markupIls > 0;
  const netLabel = orderCurrency && shown?.agentNetAmount != null
    ? formatHubwayzSaleDisplay(shown.agentNetAmount, orderCurrency)
    : shown?.agentNetIls != null
      ? formatIls(shown.agentNetIls)
      : "";
  const commissionLabel = orderCurrency && Number.isFinite(markupOrder) && !orderMarkupHidesIls
    ? formatHubwayzSaleDisplay(markupOrder, orderCurrency)
    : shown?.agentMarkupIls != null
      ? formatIls(shown.agentMarkupIls)
      : "";
  const saleLabel = orderCurrency && Number.isFinite(saleOrder) && !orderSaleHidesMarkup
    ? formatHubwayzSaleDisplay(saleOrder, orderCurrency)
    : shown?.customerSaleIls != null
      ? formatIls(shown.customerSaleIls)
      : "";
  const customerSaleIls = Number(shown?.customerSaleIls);
  const pricedOk = shown?.binding === true && Number.isFinite(customerSaleIls) && customerSaleIls > 0;
  const ourPrice = pricedOk ? { priceWithCommission: customerSaleIls } : null;

  const copyHotelLink = async () => {
    if (!hotelUrl) {
      onToast?.("אין קישור למלון");
      return;
    }
    try {
      await navigator.clipboard.writeText(hotelUrl);
      onToast?.("קישור המלון הועתק");
    } catch {
      onToast?.(hotelUrl);
    }
  };

  const createPdf = async () => {
    if (!pricedOk || pdfBusy || commissionPending) return;
    setPdfBusy(true);
    try {
      const location = [city, country].filter(Boolean).join(", ");
      const result = await openVacationPackageStyleQuotePdf(
        {
          titleHe: "הצעת מחיר - מלון",
          hotelName: hotelName || "",
          boardDetails: boardLabel || "",
          hotelCity: location,
          depDate: checkIn || "",
          retDate: checkOut || "",
          roomsInfo: [
            {
              roomName: roomName || "חדר",
              board: boardLabel || "",
              occupancy: capacityLabel || "",
            },
          ],
          flightInfo: [],
          supplementsList: [],
          totalPrice: customerSaleIls,
        },
        {
          titleSuffix: hotelName || "hotel",
          fileSlug: "hotel-quote",
          containerIdPrefix: "pdf-quote-hwz-hotel",
        }
      );
      if (result?.opened) {
        onToast?.("הדף נפתח בחלון חדש - לחץ על הדפסה ושמור כ-PDF");
      } else if (result?.downloaded) {
        onToast?.("הקובץ הורד - פתח אותו והדפס לשמירה כ-PDF");
      } else {
        onToast?.("לא ניתן ליצור PDF");
      }
    } catch (err) {
      onToast?.(err?.message || "שגיאה ביצירת PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const createCustomerOffer = async () => {
    if (!pricedOk || offerBusy || commissionPending) return;
    if (quote?.binding !== true && quote?.source !== "server_quote") {
      onToast?.("לא ניתן לשמור הצעה בלי מחיר מאומת מהשרת");
      return;
    }
    const user = getUserDetails() || {};
    const sessionID = user.sessionID || user.sessionId;
    if (!sessionID) {
      onToast?.("יש להתחבר כדי לשמור הצעה");
      return;
    }
    const currency = String(
      displayCurrency || quote.agentPriceCurrency || "USD"
    ).toUpperCase();
    const displayAmount = Number(quote.agentPriceAmount);
    setOfferBusy(true);
    try {
      const snapshot = buildPublicOfferLinkSnapshot({
        hotelid: hotelId,
        hasRoomFromContext: true,
        ctxSelectedRoom: {
          roomId: roomName || "room",
          roomName: roomName || "חדר",
          board: boardLabel || "",
          occupancyLabel: capacityLabel || "",
        },
        boardLabel,
        finalPriceIls: customerSaleIls,
        galleryImages: gallery,
        marketingOfferInput: {
          hotelName: hotelName || "",
          galleryImages: gallery,
          selectedOffer: {
            hotelName: hotelName || "",
            roomLine: roomName || "",
            boardLabel: boardLabel || "",
            totalPriceIls: customerSaleIls,
          },
        },
      });
      snapshot.public_view_model = {
        ...(snapshot.public_view_model || {}),
        hotelName: hotelName || "",
        roomName: roomName || "",
        boardLabel: boardLabel || "",
        occupancy: capacityLabel || "",
        nights,
        currency,
        quoteCurrency: currency,
        quoteDisplayAmount: Number.isFinite(displayAmount) ? displayAmount : null,
        cancellationPolicy: cancellation || "",
        description: [city, country].filter(Boolean).join(", "),
      };
      const data = await createPublicOfferLinkRequest({
        sessionID,
        payload: snapshot,
        final_price_ils: customerSaleIls,
        ttl_hours: 48,
      });
      if (!data?.success || !data?.token) {
        throw new Error(data?.error?.message || "יצירת ההצעה נכשלה");
      }
      const url = `${window.location.origin}/offers/public/${data.token}`;
      setOfferUrl(url);
      try {
        await navigator.clipboard.writeText(url);
        onToast?.("ההצעה נשמרה והקישור הועתק");
      } catch {
        onToast?.("ההצעה נשמרה");
      }
    } catch (err) {
      onToast?.(err?.response?.data?.error?.message || err?.message || "יצירת ההצעה נכשלה");
    } finally {
      setOfferBusy(false);
    }
  };

  return (
    <section className="hpm-agent-compact usm-fh-agent-tools" dir="rtl" aria-label="כלים לסוכן">
      <header className="hpm-agent-compact__head">
        <h2>כלים לסוכן</h2>
        <span className="hpm-agent-compact__badge">סוכן</span>
      </header>
      <HandlingFeePicker
        layoutVariant="hpm"
        showQuickChips
        extendHandleFeeDefault
        fixedFeeStoredCurrency="ILS"
        applyHandleFeeToPriceImmediately={false}
        orderId="newOrder"
        handleFeeData={handleFeeData}
        updateHandleFeeData={updateHandleFeeData}
        ourPrice={ourPrice}
        customerPriceIls={pricedOk ? customerSaleIls : null}
      />
      {pricedOk ? (
        <p className="usm-fh-agent-tools__quote">
          נטו לסוכן: {netLabel}
          {" · "}
          עמלת סוכן: {commissionLabel}
          {" · "}
          מחיר ללקוח: {saleLabel}
        </p>
      ) : (
        <p className="usm-fh-agent-tools__quote">ממתין למחיר מאומת מהשרת.</p>
      )}
      <div className="usm-fh-agent-tools__actions">
        <button
          type="button"
          className="hpm-agent-compact__offer"
          onClick={createPdf}
          disabled={!pricedOk || pdfBusy || commissionPending}
        >
          <FilePdfOutlined aria-hidden />
          {pdfBusy ? "מכין PDF…" : "יצירת PDF"}
        </button>
        <button
          type="button"
          className="hpm-agent-compact__offer"
          onClick={createCustomerOffer}
          disabled={!pricedOk || offerBusy || commissionPending || quote?.binding !== true}
        >
          <LinkOutlined aria-hidden />
          {offerBusy ? "שומר הצעה…" : "הצעת מחיר"}
        </button>
        <button
          type="button"
          className="hpm-agent-compact__offer"
          onClick={copyHotelLink}
          disabled={!hotelUrl}
        >
          <LinkOutlined aria-hidden />
          קישור למלון
        </button>
      </div>
      {offerUrl ? (
        <p className="hwz-quote-link">
          <a href={offerUrl}>{offerUrl}</a>
        </p>
      ) : null}
    </section>
  );
}
